// Residential stair math + IRC R311.7 sanity checks.
// Given total rise (floor-to-floor) and a target tread depth, derive the
// number of risers, exact riser height, number of treads, total run, and
// stringer length. Surface IRC compliance flags so a DIYer can spot non-code
// proportions before they cut wood.
//
// IRC R311.7.5 (residential):
//   - Max riser height: 7.75 in (197 mm)
//   - Min tread depth (run): 10 in (254 mm)
//   - Max variation between risers / between treads: 3/8 in (9.5 mm)
//   - Min headroom: 6 ft 8 in (80 in)
//
// We do NOT enforce — we flag. The user is the builder; we hand them numbers.
import { round } from '../lib/format'
import type { AccessoryLine } from '../data/companions'
import type { Locale } from '../i18n/locale'

export interface StairInputs {
  totalRiseIn: number         // finished floor to finished floor, in inches
  targetRiserIn: number       // preferred riser height (we round riser count to nearest)
  treadDepthIn: number        // tread depth (run), no nosing
  noseIn: number              // nosing overhang per tread (typically 0.75-1.25 in)
}

export interface StairFlag {
  level: 'ok' | 'warn' | 'fail'
  message: string
}

export interface StairResult {
  riserCount: number
  riserHeightIn: number
  treadCount: number          // riserCount - 1 (top "tread" is the upper floor)
  totalRunIn: number          // treadCount × treadDepth
  stringerLengthIn: number    // hypotenuse of (totalRise, totalRun)
  angleDeg: number
  flags: StairFlag[]
  lines: AccessoryLine[]
}

export const DEFAULT_STAIR: StairInputs = {
  totalRiseIn: 108,           // standard 9 ft floor-to-floor
  targetRiserIn: 7.5,
  treadDepthIn: 11,
  noseIn: 1,
}

const IRC_MAX_RISER = 7.75
const IRC_MIN_TREAD = 10

export function computeStair(i: StairInputs, locale: Locale = 'en'): StairResult {
  const tr = (en: string, fr: string) => (locale === 'fr' ? fr : en)
  const totalRise = Math.max(0, i.totalRiseIn)
  const target = Math.max(1, i.targetRiserIn)
  const tread = Math.max(0, i.treadDepthIn)

  // Riser count = round(totalRise / target), at least 2
  const riserCount = totalRise > 0 ? Math.max(2, Math.round(totalRise / target)) : 0
  const riserHeight = riserCount > 0 ? totalRise / riserCount : 0
  const treadCount = Math.max(0, riserCount - 1)
  const totalRun = treadCount * tread
  const stringerLength = Math.sqrt(totalRise * totalRise + totalRun * totalRun)
  const angle = totalRun > 0 ? (Math.atan2(totalRise, totalRun) * 180) / Math.PI : 0

  const flags: StairFlag[] = []
  if (totalRise > 0) {
    if (riserHeight > IRC_MAX_RISER + 0.01) {
      flags.push({
        level: 'fail',
        message: tr(
          `Riser height ${riserHeight.toFixed(2)} in exceeds the IRC residential maximum of 7.75 in. Add one more riser to bring it back in spec.`,
          `La hauteur de contremarche de ${riserHeight.toFixed(2)} po dépasse le maximum résidentiel CNB de 7,75 po. Ajoutez une contremarche pour rester dans les normes.`,
        ),
      })
    } else if (riserHeight > 7.5) {
      flags.push({
        level: 'warn',
        message: tr(
          `Riser height ${riserHeight.toFixed(2)} in is close to the 7.75 in code limit. Most builders aim for 7 to 7.5 in for comfort.`,
          `La hauteur de contremarche de ${riserHeight.toFixed(2)} po est près de la limite de code de 7,75 po. La plupart des constructeurs visent 7 à 7,5 po pour le confort.`,
        ),
      })
    }
    if (tread > 0 && tread < IRC_MIN_TREAD) {
      flags.push({
        level: 'fail',
        message: tr(
          `Tread depth ${tread.toFixed(2)} in is below the IRC residential minimum of 10 in.`,
          `La profondeur de marche de ${tread.toFixed(2)} po est inférieure au minimum résidentiel CNB de 10 po.`,
        ),
      })
    }
    // 2R + T rule of thumb: 24 ≤ 2R + T ≤ 25 for comfortable stairs
    const comfort = 2 * riserHeight + tread
    if (comfort < 23 || comfort > 26) {
      flags.push({
        level: 'warn',
        message: tr(
          `2×riser + tread = ${comfort.toFixed(1)} in. Comfortable stairs land between 24 and 25. Yours feel ${comfort < 23 ? 'cramped' : 'stretched'}.`,
          `2 × contremarche + marche = ${comfort.toFixed(1)} po. Un escalier confortable se situe entre 24 et 25. Le vôtre semble ${comfort < 23 ? 'serré' : 'étiré'}.`,
        ),
      })
    }
    if (angle > 37) {
      flags.push({
        level: 'warn',
        message: tr(
          `Stair angle ${angle.toFixed(1)}° is steeper than the typical residential 30–37° range.`,
          `L’angle d’escalier de ${angle.toFixed(1)}° est plus prononcé que la plage résidentielle typique de 30 à 37°.`,
        ),
      })
    }
    if (flags.length === 0) {
      flags.push({
        level: 'ok',
        message: tr(
          `All measurements fall within IRC residential stair guidelines.`,
          `Toutes les dimensions respectent les directives d’escaliers résidentiels du CNB.`,
        ),
      })
    }
  }

  const lines: AccessoryLine[] = []
  if (totalRise > 0 && riserCount > 0) {
    lines.push({
      item: 'stair stringer 2x12 pressure treated',
      label: tr('Stair stringers (2×12)', 'Limons d’escalier (2×12)'),
      kind: 'consumable',
      qty: 3,
      unit: 'piece',
      note: tr('Minimum 3 stringers (left, right, center) for treads up to 36 in wide — add one more for wider stairs.', 'Minimum de 3 limons (gauche, droite, centre) pour des marches jusqu’à 36 po de large — ajoutez-en un de plus pour les escaliers plus larges.'),
    })
    lines.push({
      item: 'stair tread oak pine pre-cut',
      label: tr('Stair treads', 'Marches d’escalier'),
      kind: 'consumable',
      qty: treadCount,
      unit: 'piece',
      note: tr(`${treadCount} treads at ${tread.toFixed(2)} in deep + ${i.noseIn} in nosing — buy pre-cut or rip from 5/4 stock.`, `${treadCount} marches de ${tread.toFixed(2)} po de profondeur + nez de ${i.noseIn} po — achetez prédécoupées ou refendues dans du stock 5/4.`),
    })
    lines.push({
      item: 'stair riser board 1x8 primed',
      label: tr('Riser boards', 'Contremarches'),
      kind: 'consumable',
      qty: riserCount,
      unit: 'piece',
      note: tr(`${riserCount} risers at ${riserHeight.toFixed(2)} in tall — 1×8 primed pine is standard for closed risers.`, `${riserCount} contremarches de ${riserHeight.toFixed(2)} po — le pin apprêté 1×8 est standard pour les contremarches fermées.`),
    })
    lines.push({
      item: 'stair angle bracket galvanized',
      label: tr('Stringer brackets', 'Étriers de limon'),
      kind: 'consumable',
      qty: riserCount * 3,
      unit: 'piece',
      note: tr('Steel angle brackets fasten each tread to each stringer — far stronger than nails alone.', 'Les étriers d’acier fixent chaque marche à chaque limon — beaucoup plus solides que les clous seuls.'),
    })
    lines.push({
      item: 'deck screws 3 inch exterior',
      label: tr('Construction screws', 'Vis de construction'),
      kind: 'consumable',
      qty: 1,
      unit: 'box',
      note: tr('3 in exterior screws for stringer-to-rim and tread-to-stringer connections.', 'Vis extérieures de 3 po pour les connexions limon-rive et marche-limon.'),
    })
    lines.push({
      item: 'framing square carpenter steel',
      label: tr('Framing square', 'Équerre de charpentier'),
      kind: 'tool',
      note: tr('Lays out stringer cuts directly — stair gauges (small brass clips) lock the rise and run.', 'Permet de tracer directement les coupes de limon — les guides d’escalier (petites pinces en laiton) bloquent la montée et la course.'),
    })
    lines.push({
      item: 'circular saw 7-1/4 inch worm drive',
      label: tr('Circular saw', 'Scie circulaire'),
      kind: 'tool',
      optional: true,
      note: tr('Standard tool for cutting stringer notches — finish corner cuts with a hand saw to avoid over-cutting.', 'Outil standard pour couper les encoches du limon — terminez les coins à la scie à main pour éviter de couper trop loin.'),
    })
  }

  return {
    riserCount,
    riserHeightIn: round(riserHeight, 3),
    treadCount,
    totalRunIn: round(totalRun, 2),
    stringerLengthIn: round(stringerLength, 2),
    angleDeg: round(angle, 1),
    flags,
    lines,
  }
}
